import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
	BookOpen,
	Users,
	ShieldCheck,
	Sparkles,
	Calendar,
	Mail,
	KeyRound,
	ListChecks,
	UserRound,
	PlayCircle,
	CheckCircle2,
	ArrowRight,
	Info,
} from "lucide-react";
import { isPuzzleUnlocked, PromoReveal } from "../../components/puzzle/GlitchPuzzle";
import { useAuth } from "../../AuthContext";

function Step({
	index,
	title,
	children,
	icon,
	active = false,
	stepRef,
}) {
	return (
		<div
			ref={stepRef}
			className={[
				"relative rounded-2xl border bg-white p-5 shadow-sm transition scroll-mt-32",
				active ? "border-indigo-300 shadow-md" : "border-slate-200 hover:shadow-md",
			].join(" ")}
		>
			{/* Accent overlay quand actif */}
			<div
				className={[
					"pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity",
					active ? "opacity-100" : "",
				].join(" ")}
				style={{
					background:
						"linear-gradient(135deg, rgba(99,102,241,0.10), rgba(249,115,22,0.06))",
				}}
			/>

			<div className="relative flex items-start gap-4">
				<div
					className={[
						"flex h-12 w-12 items-center justify-center rounded-2xl text-white ring-4 shadow-sm",
						active ? "bg-indigo-600 ring-indigo-100" : "bg-indigo-900 ring-indigo-100",
					].join(" ")}
				>
					<span className="text-orange-200">{icon}</span>
				</div>

				<div className="flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<span
							className={[
								"inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold",
								active ? "bg-indigo-600 text-white" : "bg-slate-900 text-white",
							].join(" ")}
						>
							Étape {index}
						</span>
						<h3 className="text-base font-semibold text-slate-900">{title}</h3>
					</div>
					<div className="mt-2 text-sm text-slate-600 leading-relaxed">
						{children}
					</div>
				</div>
			</div>
		</div>
	);
}

function Card({ title, icon, children }) {
	return (
		<div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
			<div className="flex items-center gap-3">
				<div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-900 text-white ring-4 ring-orange-100 shadow-sm">
					<span className="text-orange-300">{icon}</span>
				</div>
				<h3 className="text-base font-semibold text-slate-900">{title}</h3>
			</div>
			<div className="mt-3 text-sm text-slate-600 leading-relaxed">{children}</div>
		</div>
	);
}

function Tabs({ value, onChange }) {
	const items = [
		{ key: "organisateur", label: "Organisateur", icon: <ShieldCheck className="h-5 w-5" /> },
		{ key: "joueur", label: "Joueur", icon: <Users className="h-5 w-5" /> },
	];

	return (
		<div className="inline-flex rounded-2xl border border-white/15 bg-white/5 p-1 backdrop-blur">
			{items.map((t) => {
				const active = value === t.key;
				return (
					<button
						key={t.key}
						onClick={() => onChange(t.key)}
						className={[
							"flex items-center gap-2 px-5 py-3 rounded-xl text-sm sm:text-base font-semibold transition",
							active
								? "bg-white text-slate-900 shadow-sm"
								: "text-indigo-100 hover:bg-white/10",
						].join(" ")}
						aria-pressed={active}
					>
						<span className={active ? "text-indigo-600" : "text-indigo-200"}>
							{t.icon}
						</span>
						{t.label}
					</button>
				);
			})}
		</div>
	);
}


function StepsProgress({ steps, activeIndex, onGo, floating, containerRef }) {
	const pct =
		steps.length <= 1 ? 100 : Math.round((activeIndex / (steps.length - 1)) * 100);

	return (
		<div
			ref={containerRef}
			className={[
				floating ? "fixed top-0 left-0 right-0 z-50" : "relative z-10",
			].join(" ")}
		>
			<div className="border-b border-slate-200 bg-slate-50/90 backdrop-blur">
				<div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
					<div className="flex flex-col gap-2">
						<div className="flex items-center justify-between">
							<p className="text-xs font-semibold tracking-wide text-slate-700">
								Progression
							</p>
							<p className="text-xs text-slate-500">
								Étape {activeIndex + 1} / {steps.length}
							</p>
						</div>

						<div className="h-2 w-full rounded-full bg-slate-200 overflow-hidden">
							<div
								className="h-full rounded-full bg-indigo-600 transition-all"
								style={{ width: `${pct}%` }}
							/>
						</div>

						<div className="flex gap-2 overflow-x-auto pb-1">
							{steps.map((s, i) => {
								const done = i < activeIndex;
								const active = i === activeIndex;
								return (
									<button
										key={s.id}
										onClick={() => onGo(i)}
										className={[
											"shrink-0 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition",
											active
												? "border-indigo-300 bg-white text-slate-900"
												: "border-slate-200 bg-white text-slate-700 hover:border-indigo-200",
										].join(" ")}
										title={s.title}
									>
										<span
											className={[
												"inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px]",
												done
													? "bg-indigo-600 text-white"
													: active
														? "bg-slate-900 text-white"
														: "bg-slate-200 text-slate-700",
											].join(" ")}
										>
											{done ? "✓" : i + 1}
										</span>
										<span className="whitespace-nowrap">{s.short}</span>
									</button>
								);
							})}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function Guide() {
	const { token } = useAuth(); // pour conditionner les boutons
	const [tab, setTab] = useState("organisateur");

	// reset progression quand on change d’onglet
	const [activeIdx, setActiveIdx] = useState(0);

	const [floating, setFloating] = useState(false);
	const progressStartRef = useRef(null);
	const progressEndRef = useRef(null);
	const progressBarRef = useRef(null);
	const [barH, setBarH] = useState(0);
	const lockUntilRef = useRef(0);

	const isOrg = tab === "organisateur";

	const stepsOrg = useMemo(
		() => [
			{ id: "org-1", short: "Compte", title: "Créer un compte", icon: <UserRound className="h-6 w-6" /> },
			{ id: "org-2", short: "Jeu", title: "Choisir un jeu", icon: <BookOpen className="h-6 w-6" /> },
			{ id: "org-3", short: "Achat", title: "Acheter des sessions", icon: <ListChecks className="h-6 w-6" /> },
			{ id: "org-4", short: "Codes", title: "Récupérer les codes", icon: <KeyRound className="h-6 w-6" /> },
			{ id: "org-5", short: "Inviter", title: "Informer les participants", icon: <Mail className="h-6 w-6" /> },
			{ id: "org-6", short: "Équipes", title: "Créer des équipes", icon: <Users className="h-6 w-6" /> },
			{ id: "org-7", short: "Suivi", title: "Suivre les sessions", icon: <Calendar className="h-6 w-6" /> },
		],
		[]
	);

	const stepsPlayer = useMemo(
		() => [
			{ id: "p-1", short: "Page jeu", title: "Aller sur la page de jeu", icon: <PlayCircle className="h-6 w-6" /> },
			{ id: "p-2", short: "Code session", title: "Entrer le code de session", icon: <KeyRound className="h-6 w-6" /> },
			{ id: "p-3", short: "Équipe", title: "Nom d’équipe (optionnel)", icon: <Users className="h-6 w-6" /> },
			{ id: "p-4", short: "Profil", title: "Créer votre profil joueur", icon: <UserRound className="h-6 w-6" /> },
			{ id: "p-5", short: "Lobby", title: "Salle d’attente", icon: <BookOpen className="h-6 w-6" /> },
			{ id: "p-6", short: "Lancer la partie", title: "Lancer la partie", icon: <CheckCircle2 className="h-6 w-6" /> },
		],
		[]
	);

	const steps = isOrg ? stepsOrg : stepsPlayer;

	// refs pour scroll vers chaque étape
	const stepRefs = useRef([]);
	stepRefs.current = steps.map((_, i) => stepRefs.current[i] || React.createRef());

	useEffect(() => {
		setActiveIdx(0);
	}, [tab]);

	useEffect(() => {
		const els = stepRefs.current.map(r => r?.current).filter(Boolean);
		if (els.length === 0) return;

		const getAnchorY = () => {
			if (floating) return barH + 24;        // juste sous la barre sticky
			return Math.min(160, window.innerHeight * 0.25); // ligne de lecture plus basse
		};

		const computeActive = () => {
			if (Date.now() < lockUntilRef.current) return;

			const anchor = getAnchorY(); // coordonnée dans le viewport (px)

			// ✅ vrai bas de page (ne dépend pas de la taille des cards)
			const doc = document.documentElement;
			const atBottom = window.scrollY + window.innerHeight >= doc.scrollHeight - 2;
			if (atBottom) {
				setActiveIdx(els.length - 1);
				return;
			}

			// 1) l'étape qui "contient" la ligne d'ancrage
			for (let i = 0; i < els.length; i++) {
				const r = els[i].getBoundingClientRect();
				if (r.top <= anchor && r.bottom >= anchor) {
					setActiveIdx(i);
					return;
				}
			}

			// 2) Fallback: dernière étape dont le top est passé au-dessus de l'ancre
			let idx = 0;
			for (let i = 0; i < els.length; i++) {
				const r = els[i].getBoundingClientRect();
				if (r.top <= anchor + 1) idx = i;
				else break;
			}
			setActiveIdx(idx);
		};

		computeActive();
		window.addEventListener("scroll", computeActive, { passive: true });
		window.addEventListener("resize", computeActive);
		return () => {
			window.removeEventListener("scroll", computeActive);
			window.removeEventListener("resize", computeActive);
		};
	}, [tab, floating, barH]);


	useEffect(() => {
		const startEl = progressStartRef.current;
		const endEl = progressEndRef.current;
		if (!startEl || !endEl) return;

		const NAV_OFFSET = 0;      // si tu veux tenir compte d'une navbar sticky, mets genre 72
		const END_PADDING = 24;    // marge avant d’éteindre le floating

		const compute = () => {
			const startTop = startEl.getBoundingClientRect().top;
			const endTop = endEl.getBoundingClientRect().top;

			// Floating uniquement quand:
			// - on a scroll "au-dessus" du start (startTop <= offset)
			// - et qu’on n’a pas encore atteint la fin (endTop > offset + padding)
			const shouldFloat = startTop <= NAV_OFFSET && endTop > NAV_OFFSET + END_PADDING;

			setFloating(shouldFloat);
		};

		compute();
		window.addEventListener("scroll", compute, { passive: true });
		window.addEventListener("resize", compute);

		return () => {
			window.removeEventListener("scroll", compute);
			window.removeEventListener("resize", compute);
		};
	}, [tab]);

	useEffect(() => {
		const el = progressBarRef.current;
		if (!el) return;

		const measure = () => setBarH(el.getBoundingClientRect().height || 0);
		measure();

		window.addEventListener("resize", measure);
		return () => window.removeEventListener("resize", measure);
	}, [tab]);

	const goTo = (i) => {
		const el = stepRefs.current[i]?.current;
		if (!el) return;

		lockUntilRef.current = Date.now() + 1200;
		setActiveIdx(i);

		const top = el.getBoundingClientRect().top + window.scrollY;
		const offset = (floating ? barH : 0) + 16;
		window.scrollTo({ top: top - offset, behavior: "smooth" });

		setTimeout(() => {
			lockUntilRef.current = 0;
		}, 1300);
	};

	return (
		<div className="bg-slate-50 text-slate-900">
			{/* HERO */}
			<section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-900">
				<div
					className="pointer-events-none absolute inset-0 opacity-80"
					style={{
						background:
							"radial-gradient(circle at 0% 0%, rgba(129,140,248,0.45) 0, transparent 55%), radial-gradient(circle at 90% 10%, rgba(236,72,153,0.22) 0, transparent 55%), radial-gradient(circle at 50% 100%, rgba(56,189,248,0.16) 0, transparent 60%)",
					}}
				/>
				<div
					className="pointer-events-none absolute inset-0 opacity-[0.10]"
					style={{
						backgroundImage:
							"linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)",
						backgroundSize: "56px 56px",
						maskImage: "radial-gradient(circle at 50% 35%, black 0%, transparent 70%)",
						WebkitMaskImage:
							"radial-gradient(circle at 50% 35%, black 0%, transparent 70%)",
					}}
				/>

				{/* Scanline */}
				<div
					className="pointer-events-none absolute inset-0 opacity-[0.11]"
					style={{
						backgroundImage:
							"repeating-linear-gradient(to bottom, rgba(255,255,255,0.55) 0px, rgba(255,255,255,0.55) 1px, transparent 2px, transparent 7px)",
						maskImage: "radial-gradient(circle at 50% 35%, black 0%, transparent 72%)",
						WebkitMaskImage:
							"radial-gradient(circle at 50% 35%, black 0%, transparent 72%)",
					}}
				/>

				{/* Accent néon orange */}
				<div
					className="pointer-events-none absolute -bottom-40 -right-40 h-[420px] w-[420px] rounded-full opacity-30 blur-3xl"
					style={{
						background:
							"radial-gradient(circle at 30% 30%, rgba(249,115,22,0.85) 0%, rgba(249,115,22,0.0) 60%)",
					}}
				/>

				<div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
					<div className="max-w-3xl">
						<div className="flex flex-wrap items-center gap-2 mb-5">
							<span className="text-[11px] font-semibold tracking-[0.28em] text-indigo-200/90 uppercase">
								Documentation
							</span>

							<span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-2 py-1 text-[11px] font-medium text-indigo-100">
								<Sparkles className="h-3.5 w-3.5 text-orange-300" />
								Guide rapide
							</span>
						</div>

						<h1 className="text-3xl sm:text-4xl font-semibold leading-tight text-white">
							Guide d’utilisation
							<span className="text-orange-300">.</span>
						</h1>

						<p className="mt-4 text-sm sm:text-base text-indigo-100/90 max-w-2xl">
							Tout ce qu’il faut pour organiser une session (côté entreprise) et rejoindre
							une partie (côté joueurs), en quelques étapes simples.
						</p>
					</div>

					{/* Tabs plus visibles */}
					<div className="mt-10">
						<Tabs value={tab} onChange={setTab} />
					</div>
				</div>
			</section>

			{/* CONTENT */}
			<section className="py-10 sm:py-12 bg-slate-50">
				<div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
					{/* Quick prereqs row */}
					<div className="grid gap-6 md:grid-cols-3">
						{isOrg ? (
							<>
								<Card title="Objectif" icon={<BookOpen className="h-5 w-5" />}>
									Acheter des sessions, récupérer les codes, organiser vos équipes et suivre le statut
									des parties depuis votre espace.
								</Card>
								<Card title="Où ça se passe ?" icon={<Info className="h-5 w-5" />}>
									La gestion se fait depuis <span className="font-medium">Profil</span> une fois connecté.
								</Card>
								<Card
									title="Conseil"
									icon={
										<span
											style={{ cursor: isPuzzleUnlocked() ? "pointer" : "default"}}
											onClick={() => {
												if (isPuzzleUnlocked()) {
													window.dispatchEvent(new Event("blackout:reveal-promo"));
												}
											}}
										>
											<Sparkles className="h-5 w-5" />
										</span>
									}
								>
									Si vous utilisez le créateur d'équipe, vous pouvez générer des mails prêts à copier-coller.
									<PromoReveal />
								</Card>
							</>
						) : (
							<>
								<Card title="Groupe" icon={<Users className="h-5 w-5" />}>
									Entre <span className="font-medium">4 et 6 joueurs</span> par session.
								</Card>
								<Card title="Pré-requis" icon={<Mail className="h-5 w-5" />}>
									Utiliser un outil de visio / communication (Teams, Meet, Zoom…).
								</Card>
								<Card title="À garder sous la main" icon={<KeyRound className="h-5 w-5" />}>
									Le <span className="font-medium">code session</span> + la date/heure reçues par mail.
								</Card>
							</>
						)}
					</div>

					{/* Header + CTAs */}
					<div className="mt-10">
						<div className="flex items-end justify-between gap-4 flex-wrap">
							<div>
								<h2 className="text-2xl sm:text-3xl font-semibold text-slate-900">
									{isOrg ? "Parcours Organisateur" : "Parcours Joueur"}
								</h2>
								<p className="mt-2 text-sm text-slate-500 max-w-2xl">
									{isOrg
										? "De l’inscription à l’envoi des infos aux participants, puis le suivi des sessions."
										: "De la réception du mail jusqu’au lancement de la partie, étape par étape."}
								</p>
							</div>

							<div className="flex gap-2">
								{isOrg ? (
									<>
										{/* Afficher seulement si pas connecté */}
										{!token && (
											<>
												<Link
													to="/signup"
													className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:border-indigo-400 hover:text-indigo-700 transition"
												>
													S’inscrire <ArrowRight className="h-4 w-4" />
												</Link>
												<Link
													to="/login"
													className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 transition"
												>
													Se connecter <ArrowRight className="h-4 w-4" />
												</Link>
											</>
										)}

										{/* Si connecté, tu peux mettre un CTA utile */}
										{token && (
											<Link
												to="/profile"
												className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 transition"
											>
												Aller au profil <ArrowRight className="h-4 w-4" />
											</Link>
										)}
									</>
								) : (
									<Link
										to="/play"
										className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 transition"
									>
										Jouer <PlayCircle className="h-4 w-4" />
									</Link>
								)}
							</div>
						</div>
					</div>

					{/* Progress sticky + chips */}
					<div className="mt-6">
						{floating && <div style={{ height: barH }} />}

						<StepsProgress
							steps={steps}
							activeIndex={activeIdx}
							onGo={goTo}
							floating={floating}
							containerRef={progressBarRef}
						/>
					</div>

					{/* Steps list */}
					<div ref={progressStartRef} />
					<div className="mt-6 grid gap-5">
						{isOrg ? (
							<>
								<Step
									index={1}
									title="Créer un compte"
									icon={stepsOrg[0].icon}
									active={activeIdx === 0}
									stepRef={stepRefs.current[0]}
								>
									<ul className="list-disc pl-5 space-y-1">
										<li>Inscrivez-vous sur la plateforme.</li>
										<li>Connectez-vous pour accéder à votre espace entreprise.</li>
									</ul>
								</Step>

								<Step
									index={2}
									title="Choisir un jeu"
									icon={stepsOrg[1].icon}
									active={activeIdx === 1}
									stepRef={stepRefs.current[1]}
								>
									<ul className="list-disc pl-5 space-y-1">
										<li>
											Rendez-vous sur le{" "}
											<Link
												to="/catalogue"
												className="text-indigo-600 hover:text-indigo-500 font-medium"
											>
												catalogue
											</Link>
											.
										</li>
										<li>Ouvrez la page d’un jeu pour voir les détails.</li>
									</ul>
								</Step>

								<Step
									index={3}
									title="Acheter une ou plusieurs sessions"
									icon={stepsOrg[2].icon}
									active={activeIdx === 2}
									stepRef={stepRefs.current[2]}
								>
									<ul className="list-disc pl-5 space-y-1">
										<li>Achetez un pack (une ou plusieurs sessions).</li>
										<li>
											Vous pouvez choisir une <span className="font-medium">date</span> à l’achat
											(elle s’applique aux sessions créées), ou laisser vide et la définir plus tard
											depuis <span className="font-medium">Profil</span>.
										</li>
									</ul>
								</Step>

								<Step
									index={4}
									title="Récupérer les codes de session"
									icon={stepsOrg[3].icon}
									active={activeIdx === 3}
									stepRef={stepRefs.current[3]}
								>
									<ul className="list-disc pl-5 space-y-1">
										<li>
											Une fois l’achat confirmé, les codes session générés sont visibles dans{" "}
											<span className="font-medium">Profil</span>.
										</li>
										<li>Chaque code correspond à une session jouable.</li>
									</ul>
								</Step>

								<Step
									index={5}
									title="Informer les participants"
									icon={stepsOrg[4].icon}
									active={activeIdx === 4}
									stepRef={stepRefs.current[4]}
								>
									<p className="mb-2 text-sm text-slate-600">
										Deux options, selon votre organisation :
									</p>
									<ul className="list-disc pl-5 space-y-1">
										<li>Envoyer manuellement les infos (date, code session, consignes) par mail.</li>
										<li>
											Ou utiliser le <span className="font-medium">créateur d’équipe</span> pour
											générer des mails.
										</li>
									</ul>
								</Step>

								<Step
									index={6}
									title="Créer des équipes (optionnel)"
									icon={stepsOrg[5].icon}
									active={activeIdx === 5}
									stepRef={stepRefs.current[5]}
								>
									<ul className="list-disc pl-5 space-y-1">
										<li>Sélectionnez un pack acheté (contenant une ou plusieurs sessions).</li>
										<li>Importez un CSV d’employés et/ou ajoutez-les à la main.</li>
										<li>Cliquez sur “Générer les équipes” avec les filtres souhaités.</li>
										<li>
											Modifiez les équipes manuellement si besoin grâce au système de drag and drop
											et d'ajout / retrait d'employés.
										</li>
										<li>Enregistrez les équipes, puis cliquez sur “Générer les mails”.</li>
										<li>Copiez-collez le contenu et envoyez-le aux participants.</li>
									</ul>
								</Step>

								<Step
									index={7}
									title="Suivre les sessions"
									icon={stepsOrg[6].icon}
									active={activeIdx === 6}
									stepRef={stepRefs.current[6]}
								>
									<ul className="list-disc pl-5 space-y-1">
										<li>
											Le suivi des sessions (statut, demandes de report…) se fait depuis{" "}
											<span className="font-medium">Profil</span>.
										</li>
										<li>Vous devez être connecté pour accéder à ces actions.</li>
									</ul>
								</Step>
							</>
						) : (
							<>
								<Step
									index={1}
									title="Aller sur la page de jeu"
									icon={stepsPlayer[0].icon}
									active={activeIdx === 0}
									stepRef={stepRefs.current[0]}
								>
									<ul className="list-disc pl-5 space-y-1">
										<li>
											Après réception du mail (date + code session), allez sur la page{" "}
											<Link to="/play" className="text-indigo-600 hover:text-indigo-500 font-medium">
												jouer
											</Link>
											.
										</li>
										<li>Gardez votre outil de visio ouvert pour communiquer.</li>
									</ul>
								</Step>

								<Step
									index={2}
									title="Entrer le code de session"
									icon={stepsPlayer[1].icon}
									active={activeIdx === 1}
									stepRef={stepRefs.current[1]}
								>
									<ul className="list-disc pl-5 space-y-1">
										<li>Entrez le code fourni dans le mail.</li>
										<li>Validez pour rejoindre la session.</li>
									</ul>
								</Step>

								<Step
									index={3}
									title="Nom d’équipe (optionnel)"
									icon={stepsPlayer[2].icon}
									active={activeIdx === 2}
									stepRef={stepRefs.current[2]}
								>
									<ul className="list-disc pl-5 space-y-1">
										<li>Choisissez un nom d’équipe si vous le souhaitez.</li>
										<li>Si vous n'en choisissez pas, un nom d'équipe automatique sera défini.</li>
									</ul>
								</Step>

								<Step
									index={4}
									title="Créer votre profil joueur"
									icon={stepsPlayer[3].icon}
									active={activeIdx === 3}
									stepRef={stepRefs.current[3]}
								>
									<ul className="list-disc pl-5 space-y-1">
										<li>
											Choisissez un pseudo <span className="font-medium">unique</span>.
										</li>
										<li>Choisissez un mot de passe (utile uniquement pour la reconnexion).</li>
										<li>Validez pour entrer dans la salle d’attente.</li>
									</ul>
								</Step>

								<Step
									index={5}
									title="Salle d’attente"
									icon={stepsPlayer[4].icon}
									active={activeIdx === 4}
									stepRef={stepRefs.current[4]}
								>
									<ul className="list-disc pl-5 space-y-1">
										<li>Lisez le tutoriel affiché.</li>
										<li>Toutes les règles de jeu sont expliquées dans le tutoriel avant le lancement.</li>
										<li>Cliquez sur “Je suis prêt”.</li>

									</ul>
								</Step>

								<Step
									index={6}
									title="Lancer la partie"
									icon={stepsPlayer[5].icon}
									active={activeIdx === 5}
									stepRef={stepRefs.current[5]}
								>
									<ul className="list-disc pl-5 space-y-1">
										<li>Quand tous les joueurs sont prêts, la partie peut être lancée.</li>
										<li>Bonne chance ✦</li>
									</ul>
								</Step>
							</>
						)}
					</div>
					<div ref={progressEndRef} />
				</div>
			</section>
		</div>
	);
}

export default Guide;
